import type { SuburbInput } from "@repo/adapter-core";
import { slugify } from "@repo/adapter-core";
import type { SoldOnlyAdapterConfig } from "../shared/sold-only.ts";

/**
 * McGrath's site is a Next.js 13+ app-router build with a catch-all
 * `[[...slug]]` route — every URL resolves to either a CMS-backed page
 * or the NotFound boundary. There is no canonical `/buy/sold/<slug>` or
 * `/buy/{type}/{state}/{region}/{suburb}/{postcode}` path; listings load
 * client-side via XHR after the SSR shell renders.
 *
 * Verified via Brightdata UI Discover & sample on 2026-05-20:
 *   /our-offices    → 404 (Next.js NotFound)
 *   /sitemap.xml    → 404 (Next.js NotFound)
 *   /buy            → 200 OK, but SSR HTML has zero __next_f payloads
 *                     and zero /property/ refs — listings hydrate
 *                     client-side
 *
 * Until McGrath gets a Brightdata Scraping Browser zone OR we capture
 * the internal listings JSON endpoint, discovery returns 0 URLs. See
 * docs/scraper-routing.md for the full smoke-test results and the
 * 3-adapter SPA-hydration class (property.com.au, place-estate-agents,
 * mcgrath).
 *
 * The URL below is the canonical client-side path — it still includes
 * the suburb-postcode slug so we keep adapter-test contract compliance
 * (URL must reference the suburb), but the server returns NotFound for
 * it until McGrath's site-wide SPA-rendered routing is in scope. A
 * JS-rendered fetch path (Brightdata Scraping Browser or direct JSON
 * endpoint capture) is required for non-zero discovery.
 */
export function buildSearchUrl(suburb: SuburbInput, _config: SoldOnlyAdapterConfig): string {
  const query = `${slugify(suburb.locality)}-${suburb.state.toLowerCase()}-${suburb.postcode}`;
  return `https://www.mcgrath.com.au/buy/sold/${query}`;
}
